import React, { useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { CircularProgress } from "@material-ui/core";
import { CheckOutlined } from "@material-ui/icons";

import CategoryServices from "../../services/CategoryServices";

const useStyles = makeStyles(() => ({
  root: {
    padding: "8px 8px 4px",
    borderTop: "1px solid #ececec",
    borderBottom: "1px solid #ececec",
  },
  label: {
    fontSize: 11,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 0.4,
    color: "#555",
    padding: "4px 8px 6px",
  },
  row: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    width: "100%",
    minHeight: 48,
    padding: "6px 8px",
    border: 0,
    borderRadius: 8,
    background: "transparent",
    cursor: "pointer",
    textAlign: "left",
    font: "inherit",
    color: "#1a1a1a",
    "&:hover": { background: "#f5f5f5" },
    "&:focus-visible": { outline: "2px solid #1a1a1a", outlineOffset: 1 },
    "&:disabled": { cursor: "default" },
  },
  rowActive: {
    background: "#e6e6e6",
    "&:hover": { background: "#e6e6e6" },
  },
  avatar: {
    flex: "0 0 32px",
    width: 32,
    height: 32,
    borderRadius: "50%",
    background: "#3b3b3b",
    color: "#fff",
    fontSize: 13,
    fontWeight: 500,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  text: { flex: 1, minWidth: 0 },
  name: {
    fontSize: 14,
    fontWeight: 500,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  sub: { fontSize: 13, color: "#555" },
  trailing: { flex: "0 0 18px", display: "flex", color: "#1a1a1a" },
  error: { fontSize: 13, color: "#b3261e", padding: "4px 8px" },
}));

const initialsOf = (name) =>
  String(name || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("") || "S";

/**
 * Parent profile-menu section listing the children linked to the session and
 * switching the active one. Renders nothing unless the session can switch
 * (mobile-number login with more than one active child).
 */
export default function StudentSwitcher() {
  const classes = useStyles();
  const [data, setData] = useState(null);
  const [switchingId, setSwitchingId] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    CategoryServices.getMyStudents()
      .then((res) => {
        if (alive) setData(res);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  if (!data || !data.canSwitch || !Array.isArray(data.students)) return null;

  const activeId = String(data.activeStudentId || "");

  const handleSelect = (studentId) => {
    if (switchingId || String(studentId) === activeId) return;
    setError("");
    setSwitchingId(String(studentId));
    CategoryServices.switchStudent(studentId)
      .then((res) => {
        // Same shape as the login response: replace the stored session details.
        localStorage.setItem("userDetail", JSON.stringify(res));
        if (res.screens) localStorage.setItem("rbac_screens", JSON.stringify(res.screens));
        if (res.permissions) localStorage.setItem("rbac_permissions", JSON.stringify(res.permissions));
        // Reload so every page and the socket pick up the new student session.
        window.location.reload();
      })
      .catch((err) => {
        setSwitchingId(null);
        setError(err?.response?.data?.message || "Could not switch student. Please try again.");
      });
  };

  return (
    <div className={classes.root} role="group" aria-label="Switch student">
      <div className={classes.label}>Switch student</div>
      {data.students.map((s) => {
        const id = String(s.studentId);
        const isActive = id === activeId;
        return (
          <button
            key={id}
            type="button"
            className={`${classes.row} ${isActive ? classes.rowActive : ""}`}
            aria-current={isActive ? "true" : undefined}
            disabled={Boolean(switchingId)}
            onClick={() => handleSelect(s.studentId)}
          >
            <span className={classes.avatar} aria-hidden="true">
              {initialsOf(s.name)}
            </span>
            <span className={classes.text}>
              <span className={classes.name} style={{ display: "block" }}>
                {s.name || "Student"}
              </span>
              {s.reg_id ? <span className={classes.sub}>{s.reg_id}</span> : null}
            </span>
            <span className={classes.trailing}>
              {switchingId === id ? (
                <CircularProgress size={16} thickness={5} color="inherit" />
              ) : isActive ? (
                <CheckOutlined style={{ fontSize: 18 }} aria-label="Current student" />
              ) : null}
            </span>
          </button>
        );
      })}
      {error ? <div className={classes.error}>{error}</div> : null}
    </div>
  );
}
